<template>
    <ChatWidgetSection title="Feedback" class="justify-between h-full">
        <template v-if="reversedRolePlayMessages.length === 0" #description>I will show you suggestions and kudos based on your role play conversation</template>
        <template #content>
            <ChatWidgetAccordion v-model="accordionModel" :accordion-ids="accordionIds" :unmount-on-hide="false" class="grow">
                <template #[makeTriggerSlot(ROLE_PLAY_ACCORDION_ID.SUGGESTION)]>
                    <ChatWidgetTitle :show-status="suggestionRolePlayMessages.length > 0">
                        <template #icon><LightBulb height="24" width="24" /></template>
                        <template #text>{{ suggestionSectionTitle }}</template>
                    </ChatWidgetTitle>
                </template>
                <template #[makeContentSlot(ROLE_PLAY_ACCORDION_ID.SUGGESTION)]>
                    <ChatRolePlayFeedback :focused-feedback="focusedFeedback" :role-play-messages="suggestionRolePlayMessages" :ref-for-line="refForLine" />
                </template>
                <template #[makeTriggerSlot(ROLE_PLAY_ACCORDION_ID.PRAISE)]>
                    <ChatWidgetTitle :show-status="praiseRolePlayMessages.length > 0">
                        <template #icon><AwardBadge height="24" width="24" /></template>
                        <template #text>{{ praiseSectionTitle }}</template>
                    </ChatWidgetTitle>
                </template>
                <template #[makeContentSlot(ROLE_PLAY_ACCORDION_ID.PRAISE)]>
                    <ChatRolePlayFeedback :focused-feedback="focusedFeedback" :role-play-messages="praiseRolePlayMessages" :ref-for-line="refForLine" />
                </template>
            </ChatWidgetAccordion>
            <div>
                <HorizontalDivider />
                <p class="body-small-bold flex items-center gap-2 mb-2">
                    <CoachingModeMarble class="size-8" aria-hidden="true" />
                    {{ t("chat.rolePlay.style") }}
                </p>
                <BaseSelect
                    id="role-play-style"
                    :aria-label="t('chat.rolePlay.style')"
                    :model-value="currentRolePlayAdjustValue ?? undefined"
                    :options="rolePlayAdjustments.map(([key, label]) => ({ key, label }))"
                    @update:model-value="selectAdjustment" />
            </div>
        </template>
    </ChatWidgetSection>
</template>

<script setup lang="ts">
import { type GlobalProperties } from "~vue/appTypes";
import { ChatAction, getRolePlayMonitorParams, isRolePlayMessage, pluckAction } from "~vue/chatActions.js";
import ChatWidgetAccordion, { makeContentSlot, makeTriggerSlot } from "~vue/ChatWidgets/ChatWidgetAccordion.vue";
import ChatWidgetSection from "~vue/ChatWidgets/ChatWidgetSection.vue";
import BaseSelect from "~vue/components/BaseSelect.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import CoachingModeMarble from "~vue/components/navigation/CoachingModeMarble.vue";
import { CHAT_EVENT } from "~vue/events";
import AwardBadge from "~vue/icons/AwardBadge.vue";
import LightBulb from "~vue/icons/LightBulb.vue";
import { type ChatMessage, type RolePlayAdjustment, RolePlayAdjustmentEnum, type RolePlayAdjustmentProp } from "~vue/types/chat";
import { logUserInteraction } from "~vue/utils/logUtils";
import type { AcceptableValue } from "reka-ui";
import { type ComponentPublicInstance, computed, inject, onMounted, onUnmounted, ref } from "vue";
import { useI18n } from "vue-i18n";

import ChatRolePlayFeedback from "./ChatRolePlayFeedback.vue";
import ChatWidgetTitle from "./ChatWidgetTitle.vue";

const { t } = useI18n();

const ROLE_PLAY_ACCORDION_ID = {
    SUGGESTION: "suggestion",
    PRAISE: "praise",
};

const accordionIds = Object.values(ROLE_PLAY_ACCORDION_ID);

type RolePlayInsightsProps = {
    rolePlayAdjustments?: RolePlayAdjustmentProp;
    rolePlayAdjustmentDefault?: RolePlayAdjustment;
    messages: ChatMessage[];
};

const { rolePlayAdjustments = [], rolePlayAdjustmentDefault = RolePlayAdjustmentEnum.IS_OK, messages } = defineProps<RolePlayInsightsProps>();

const { emitter } = inject<GlobalProperties>("globalProperties", {} as GlobalProperties);
const coachingSessionUuid = inject<string>("coachingSessionUuid", "");
const actionRefs = ref(new Map());
const focusedFeedback = ref<number | null>(null);

const reversedRolePlayMessages = computed(() => messages.filter((m) => isRolePlayMessage(m)).toReversed());
const suggestionRolePlayMessages = computed(() => reversedRolePlayMessages.value.filter((m) => getRolePlayMonitorParams(m)?.sentiment === "suggestion"));
const praiseRolePlayMessages = computed(() => reversedRolePlayMessages.value.filter((m) => getRolePlayMonitorParams(m)?.sentiment === "praise"));
const suggestionSectionTitle = computed(() => {
    const count = suggestionRolePlayMessages.value.length;
    if (count === 0) {
        return "Suggestions";
    }
    return `${count} Suggestion${count === 1 ? "" : "s"}`;
});
const praiseSectionTitle = computed(() => {
    const count = praiseRolePlayMessages.value.length;
    if (count === 0) {
        return "Kudos";
    }
    return `${count} Kudos`;
});

const lastRolePlayAdjustAction = computed(() => {
    return pluckAction(messages.toReversed(), ChatAction.ROLE_PLAY_ADJUST, true);
});

const currentRolePlayAdjustValue = computed(() => {
    if (lastRolePlayAdjustAction.value) {
        return lastRolePlayAdjustAction.value.line.action_params.adjustment;
    }

    return rolePlayAdjustmentDefault;
});

function selectAdjustment(value: AcceptableValue) {
    const option = rolePlayAdjustments.find(([key]) => key === value);
    if (option) {
        handleAdjustment(option[0]);
    }
}

function refForLine(element: Element | ComponentPublicInstance | null, messageId: number) {
    if (element) {
        actionRefs.value.set(messageId, element);
    }
}

function lastPersistedChatMessageId(): number | undefined {
    for (let i = messages.length - 1; i >= 0; i--) {
        const id = messages[i].chat_message_id;
        if (typeof id === "number" && id > 0) {
            return id;
        }
    }
    return undefined;
}

function handleAdjustment(adjustment: RolePlayAdjustment) {
    const messageId = lastPersistedChatMessageId();
    let lineIdx: number | undefined;
    if (lastRolePlayAdjustAction.value && lastRolePlayAdjustAction.value.message.chat_message_id === messageId) {
        lineIdx = lastRolePlayAdjustAction.value.lineIdx;
    }

    emitter.emit(ChatAction.ROLE_PLAY_ADJUST, {
        message_id: messageId,
        lineIdx,
        action_state: {
            submitted: true,
        },
        action_params: {
            adjustment,
        },
    });
    logUserInteraction("role_play_adjust_submitted", { adjustment }, coachingSessionUuid);
}

const accordionModel = ref<string[]>([]);

function focusRolePlayFeedback(messageId: number) {
    const SCROLL_INTO_VIEW_DELAY = 200;
    const REMOVE_FOCUS_DELAY = 1000;
    const actionRef = actionRefs.value.get(messageId);

    // handles opening the correct accordion section housing the feedback msg
    const message = messages.find((m) => m.chat_message_id === messageId);
    if (!message) {
        return;
    }

    const feedbackSentiment = getRolePlayMonitorParams(message)?.sentiment;
    if (feedbackSentiment && !accordionModel.value.includes(feedbackSentiment)) {
        // adds feedback sentiment into the accordionModel state so that the accordion opens
        accordionModel.value = [...accordionModel.value, feedbackSentiment];
    }
    // short delay added to ensure the accordion open animation completes before scrolling
    setTimeout(() => {
        if (actionRef) {
            actionRef.scrollIntoView({ behavior: "smooth" });
            focusedFeedback.value = messageId;
            setTimeout(() => {
                focusedFeedback.value = null;
            }, REMOVE_FOCUS_DELAY);
        }
    }, SCROLL_INTO_VIEW_DELAY);
}

onMounted(() => {
    emitter.on(CHAT_EVENT.FOCUS_ROLE_PLAY_MESSAGE_FEEDBACK, focusRolePlayFeedback);
});

onUnmounted(() => {
    emitter.off(CHAT_EVENT.FOCUS_ROLE_PLAY_MESSAGE_FEEDBACK, focusRolePlayFeedback);
});
</script>
